import { Link } from 'react-router-dom';
import { useVorstellungsgespraechListe } from '../../../api/vorstellungsgespraech';
import type { Bewerber } from '../../../types/bewerber';
import {
  VorstellungsgespraechStatus,
  type Vorstellungsgespraech,
} from '../../../types/vorstellungsgespraech';

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

const interviewerName = (vg: Vorstellungsgespraech) =>
  vg.durchgefuehrtVon ? `${vg.durchgefuehrtVon.firstName} ${vg.durchgefuehrtVon.lastName}` : null;

/**
 * Shows the interview history on the Bewerber detail page. Renders nothing until an
 * interview exists — the "Interview Tool" action button is the entry point.
 */
export function VorstellungsgespraechCard({ bewerber }: { bewerber: Bewerber }) {
  const { data } = useVorstellungsgespraechListe(bewerber.id);
  const interviews = data?.data ?? [];

  if (interviews.length === 0) return null;

  const basePath = `/bewerber/${bewerber.id}/vorstellungsgespraech`;

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">Vorstellungsgespräch</h3>

      <ul className="space-y-2">
        {interviews.map((vg) => {
          const isDraft = vg.status === VorstellungsgespraechStatus.ENTWURF;
          const name = interviewerName(vg);

          return (
            <li key={vg.id} className="flex items-center justify-between gap-3 text-sm">
              <div className="min-w-0">
                {isDraft ? (
                  <span className="text-amber-700">
                    In Arbeit — begonnen am {formatDate(vg.createdAt)}
                  </span>
                ) : (
                  <span className="text-gray-700">
                    Abgeschlossen am {formatDate(vg.durchgefuehrtAm ?? vg.updatedAt)}
                  </span>
                )}
                {name && <span className="text-gray-500"> · {name}</span>}
              </div>

              {/* A completed interview must be addressed explicitly, otherwise opening the
                  page would start a fresh draft instead of showing this protocol. */}
              <Link
                to={isDraft ? basePath : `${basePath}?id=${vg.id}`}
                className="flex-shrink-0 text-primary-600 hover:text-primary-900 font-medium"
              >
                {isDraft ? 'Fortsetzen' : 'Anzeigen'}
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
